import React, { useState, useRef } from 'react';
import { X, Upload, Image } from 'lucide-react';
import { db, storage } from '../../firebase';
import { collection, addDoc, doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';

const CreateShowModal = ({ isOpen, onClose, userId }) => {
  const [showName, setShowName] = useState('');
  const [showDescription, setShowDescription] = useState('');
  const [showImage, setShowImage] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setShowImage(file);
      setShowImagePreview(URL.createObjectURL(file));
    }
  };
  
  const handleImageClick = () => {
    fileInputRef.current.click();
  };
  
  const validateInputs = () => {
    let isValid = true;
    
    // Validate name (max 21 characters)
    if (showName.length > 21) {
      setNameError(true);
      isValid = false;
    } else {
      setNameError(false);
    }
    
    // Validate description (max 31 words)
    const wordCount = showDescription.trim().split(/\s+/).length;
    if (showDescription.trim() !== '' && wordCount > 31) {
      setDescriptionError(true);
      isValid = false;
    } else {
      setDescriptionError(false);
    }
    
    return isValid;
  };
  
  const handleSubmit = async () => {
    if (!showName.trim() || !validateInputs()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // First, get the current user data to ensure we have the correct name and handle
      const userDoc = await getDoc(doc(db, 'users', userId));
      let userName = 'User';
      let userHandle = 'user';
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userName = userData.name || 'User';
        userHandle = userData.handle || 'user';
      }
      
      // Create the show document according to ShowModel in DatabaseModels.js
      const showData = {
        showName: showName.trim(),
        showDescription: showDescription.trim(),
        showImage: '', // Will be updated if image is uploaded
        showCreationDate: serverTimestamp(),
        showCreator: userId,
        showCollaborators: {
          [userId]: {
            id: userId,
            name: userName, // Use the fetched user name
            handle: userHandle, // Use the fetched user handle
            role: 'owner'
          }
        },
        showCastSeason: { 1: [] },
        currentSeason: 1,
        showPossibleScore: 0,
        showEarnedScore: 0,
        seasonScores: {
          1: {
            seasonEarnedScore: 0,
            seasonPossibleScore: 0
          }
        },
        showStreak: 0,
        showStreakExpirationDate: null,
        streakExpirationDate: null,
        
        // Additional fields that might be useful
        episodeCount: 0,
        lastUpdated: serverTimestamp()
      };
      
      // Add the show document to the shows collection
      const showRef = await addDoc(collection(db, 'shows'), showData);
      const showId = showRef.id;
      
      // Update the document with its own ID
      await setDoc(doc(db, 'shows', showId), { 
        id: showId,
        showId: showId
      }, { merge: true });
      
      // Upload show cover image if provided
      if (showImage) {
        const imageRef = ref(storage, `shows/${showId}/cover_${Date.now()}`);
        await uploadBytes(imageRef, showImage);
        const imageUrl = await getDownloadURL(imageRef);
        
        // Update the show document with the image URL
        await setDoc(doc(db, 'shows', showId), { showImage: imageUrl }, { merge: true });
        showData.showImage = imageUrl;
      }
      
      // Add show reference to user's shows subcollection according to UserShowModel
      await setDoc(doc(db, 'users', userId, 'shows', showId), {
        id: showId,
        showName: showName.trim(),
        addedAt: serverTimestamp()
      });
      
      // Close the modal and pass the new show data back
      onClose({ 
        id: showId, 
        showId: showId,
        ...showData 
      });
      
    } catch (err) {
      console.error('Error creating show:', err);
      setError('Failed to create show. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      zIndex: 1100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '1rem'
    }}>
      <div style={{
        padding: '1.5rem',
        backgroundColor: '#6600cc',
        borderRadius: '0.75rem',
        maxWidth: '45rem',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'white'
          }}>
            Create New Show
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Form content */}
        <div style={{
          display: 'flex',
          gap: '1.5rem',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}>
          {/* Left side - Image upload */}
          <div style={{
            width: '12rem',
            flexShrink: 0
          }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
            
            <div
              onClick={handleImageClick}
              style={{
                aspectRatio: '2/3',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '.8rem',
                display: 'flex',
                width: '12rem',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2px dashed grey',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {showImagePreview ? (
                <img
                  src={showImagePreview}
                  alt="Show cover preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                />
              ) : (
                <>
                  <Image size={32} color="#999" style={{ marginBottom: '0.5rem' }} />
                  <div style={{ color: '#999', fontSize: '0.9rem', textAlign: 'center' }}>
                    Upload Show Cover
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Right side - Form fields */}
          <div style={{
            flex: 1,
            minWidth: '15rem',
            marginRight: '2rem'
          }}>
            <div style={{display: 'flex'}}>
              {/* Show name field */}
              <div style={{ 
                marginBottom: '1.5rem',
                position: 'relative'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    color: 'white'
                  }}>
                    Show Name
                  </label>
                </div>
                
                <input
                  type="text"
                  value={showName}
                  onChange={(e) => setShowName(e.target.value)}
                  placeholder="Enter show name"
                  style={{
                    width: '20rem',
                    padding: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: nameError ? '1px solid #E50914' : 'none',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.9rem'
                  }}
                />
                {nameError && (
                  <div style={{
                    color: '#E50914',
                    fontSize: '0.8rem',
                    marginTop: '0.25rem'
                  }}>
                    Show name must be 21 characters or less
                  </div>
                )}
                <div style={{
                  fontSize: '0.8rem',
                  color: '#999',
                  marginTop: '0.25rem',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>{showName.length}/21 characters</span>
                </div>
              </div>
            </div>
            
            {/* Description field */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: 'white'
              }}>
                Description
              </label>
              <textarea
                value={showDescription}
                onChange={(e) => setShowDescription(e.target.value)}
                placeholder="Enter show description"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: descriptionError ? '1px solid #E50914' : 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.9rem',
                  minHeight: '6rem',
                  resize: 'vertical'
                }}
              />
              {descriptionError && (
                <div style={{
                  color: '#E50914',
                  fontSize: '0.8rem',
                  marginTop: '0.25rem'
                }}>
                  Description must be 31 words or less
                </div>
              )}
              <div style={{
                fontSize: '0.8rem',
                color: '#999',
                marginTop: '0.25rem',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span>{showDescription.trim().split(/\s+/).length}/31 words</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Error message */}
        {error && (
          <div style={{
            color: '#E50914',
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            {error}
          </div>
        )}
        
        {/* Create button */}
        <div style={{ marginTop: '1rem', width: '100%' }}>
          <button
            onClick={handleSubmit}
            disabled={saving || !showName.trim() || nameError || descriptionError}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: (!showName.trim() || nameError || descriptionError) 
                ? 'rgba(200, 200, 200, 0.3)' 
                : 'white',
              color: (!showName.trim() || nameError || descriptionError) 
                ? '#999' 
                : '#6600CC',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: (saving || !showName.trim() || nameError || descriptionError) 
                ? 'not-allowed' 
                : 'pointer',
              opacity: saving ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {saving ? 'Creating...' : 'Create Show'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateShowModal; 